/** 全局搜索接口返回形态不一，在此做防御式解析；联调时按真实字段微调 pick* 逻辑 */

import { format } from "date-fns";

export interface SearchMessageTag {
  id?: string | number;
  text: string;
  color?: string;
}

export interface SearchVisitorAvatarFields {
  avatar: string;
  platform: string;
  rawPlatform: string;
  device: string;
}

/** 聊天记录搜索单条命中 */
export interface SearchMessageItem {
  messageId?: string;
  highlight?: string;
  messageType?: string;
  microtime?: number;
  senderType?: string;
  workerName?: string;
  workerNickname?: string;
}

export interface SearchMessageRow extends SearchVisitorAvatarFields {
  uid: string;
  items: SearchMessageItem[];
  totalHits: number;
  matchCount?: number;
  visitor: SearchVisitorRow;
}

export interface SearchVisitorRow extends SearchVisitorAvatarFields {
  uid: string;
  name: string;
  tags?: SearchMessageTag[];
  [key: string]: unknown;
}

function asRecord(x: unknown): Record<string, unknown> | null {
  return x !== null && typeof x === "object" && !Array.isArray(x)
    ? (x as Record<string, unknown>)
    : null;
}

function toStr(v: unknown): string {
  if (v === null || v === undefined) return "";
  return String(v).trim();
}

function toNum(v: unknown, fallback = 0): number {
  if (typeof v === "number" && !Number.isNaN(v)) return v;
  if (typeof v === "string") {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? fallback : n;
  }
  return fallback;
}

function extractArray(result: unknown): unknown[] {
  if (Array.isArray(result)) return result;
  const o = asRecord(result);
  if (!o) return [];
  const keys = ["list", "data", "rows", "items", "records", "hits", "visitors", "messages"];
  for (const k of keys) {
    const v = o[k];
    if (Array.isArray(v)) return v;
  }
  const nested = asRecord(o.result);
  if (nested) {
    for (const k of keys) {
      const v = nested[k];
      if (Array.isArray(v)) return v;
    }
  }
  return [];
}

function pickUid(obj: Record<string, unknown>): string {
  const keys = [
    "uid",
    "visitorUid",
    "visitor_id",
    "user_id",
    "userId",
    "id",
  ];
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === "string" && v) return v;
    if (typeof v === "number" && v) return String(v);
  }
  return "";
}

function pickName(obj: Record<string, unknown>): string {
  const direct =
    toStr(obj.name) ||
    toStr(obj.nickname) ||
    toStr(obj.visitorName) ||
    toStr(obj.title);
  if (direct) return direct;
  const loc = toStr(obj.feLocation);
  const nick = toStr(obj.nickname);
  return `${loc} ${nick}`.trim();
}

function pickSnippet(obj: Record<string, unknown>): string {
  return (
    toStr(obj.snippet) ||
    toStr(obj.content) ||
    toStr(obj.preview) ||
    toStr(obj.message) ||
    toStr(obj.text) ||
    toStr(obj.latestMsgText)
  );
}

function pickTimestamp(obj: Record<string, unknown>): string {
  return (
    toStr(obj.timestamp) ||
    toStr(obj.time) ||
    toStr(obj.created_at) ||
    toStr(obj.createdAt) ||
    toStr(obj.msgTime)
  );
}

function pickMatchCount(obj: Record<string, unknown>): number {
  const n = toNum(
    obj.matchCount ?? obj.match_count ?? obj.messageCount ?? obj.msg_count ?? obj.total ?? obj.count,
    0,
  );
  return n > 0 ? n : 1;
}

function normalizeTag(raw: unknown): SearchMessageTag | null {
  const o = asRecord(raw);
  if (!o) return null;
  const text = toStr(o.text ?? o.name ?? o.label);
  if (!text) return null;
  return {
    id: (o.id as string | number) ?? undefined,
    text,
    color: toStr(o.color).replace(/^#/, "") || undefined,
  };
}

function pickTags(obj: Record<string, unknown>): SearchMessageTag[] {
  const raw = obj.tags;
  if (!Array.isArray(raw)) return [];
  return raw.map(normalizeTag).filter(Boolean) as SearchMessageTag[];
}

function pickVisitorMeta(obj: Record<string, unknown>): SearchVisitorAvatarFields {
  const nested = asRecord(obj.visitor) || {};
  const fromSources = (
    keys: string[],
  ): string => {
    for (const source of [obj, nested]) {
      for (const k of keys) {
        const v = toStr(source[k]);
        if (v) return v;
      }
    }
    return "";
  };

  return {
    avatar: fromSources(["avatar"]),
    platform: fromSources(["platform"]),
    rawPlatform: fromSources(["rawPlatform", "raw_platform"]),
    device: fromSources(["device"]),
  };
}




export type HighlightPart = { hit: boolean; text: string };

export interface VisitorHighlightItem {
  field: string;
  label: string;
  parts: HighlightPart[];
}

const VISITOR_HIGHLIGHT_LABELS: Record<string, string> = {
  name: "姓名",
  nickname: "昵称",
  auto_nickname: "昵称",
  mobile: "手机",
  weixin: "微信",
  qq: "QQ",
  remark: "备注",
  region_name: "地区"
};

const VISITOR_HIGHLIGHT_ORDER = [
  "name",
  "nickname",
  "auto_nickname",
  "mobile",
  "weixin",
  "qq",
  "region_name",
  "remark",
];

/** 从访客搜索接口的 custom_fields 解析字段展示名与排序 */
export function parseVisitorCustomFieldsMeta(customFields: unknown): {
  labels: Record<string, string>;
  order: string[];
} {
  const labels: Record<string, string> = {};
  const order: string[] = [];
  if (!Array.isArray(customFields)) return { labels, order };

  for (const raw of customFields) {
    const o = asRecord(raw);
    if (!o) continue;
    const fieldName = toStr(o.field_name);
    if (!fieldName) continue;

    const apiName = toStr(o.api_name);
    const fieldKey = toStr(o.field_key);
    if (apiName) labels[apiName] = fieldName;
    if (fieldKey) labels[fieldKey] = fieldName;

    const sortKey = apiName || fieldKey;
    if (sortKey && !order.includes(sortKey)) order.push(sortKey);
  }

  return { labels, order };
}

/** 预设 label 与 custom_fields 合并，custom_fields 优先 */
export function buildVisitorFieldMeta(customFields: unknown): {
  labels: Record<string, string>;
  order: string[];
} {
  const { labels: customLabels, order: customOrder } =
    parseVisitorCustomFieldsMeta(customFields);

  const labels = { ...VISITOR_HIGHLIGHT_LABELS, ...customLabels };
  const order = customOrder.length
    ? [
        ...customOrder,
        ...VISITOR_HIGHLIGHT_ORDER.filter((k) => !customOrder.includes(k)),
      ]
    : [...VISITOR_HIGHLIGHT_ORDER];

  return { labels, order };
}

/** 聊天记录搜索：microtime 转展示时间 */
export function formatSearchMessageMicrotime(microtime: unknown): string {
  let ts =
    typeof microtime === "number"
      ? microtime
      : parseInt(String(microtime ?? ""), 10);
  if (!Number.isFinite(ts) || ts <= 0) return "";
  if (String(ts).length === 16) {
    ts = Math.floor(ts / 1000);
  }
  return format(ts, "yyyy-MM-dd HH:mm");
}

/** 发送方角标文案 */
export function messageSenderTagLabel(item: SearchMessageItem): string {
  const t = String(item.senderType || "").toLowerCase();
  if (t === "worker") {
    return `${item.workerName} ${item.workerNickname || ""}`;
  }

  if (t === "ai") {
    return "AI客服";
  }

  if (t === "system") {
    return "系统";
  }

  return "访客";
}

export function isVisitorMessageSender(senderType: unknown): boolean {
  const t = String(senderType || "").toLowerCase();
  return t !== "worker" && t !== "worker_temp" && t !== "ai" && t !== "system";
}

/** 单条命中消息的高亮片段 */
export function messageItemHighlightParts(
  item: SearchMessageItem,
): HighlightPart[] {
  return parseMarkHighlight(String(item.highlight || ""));
}

/** 解析接口返回的 `<mark>...</mark>` 高亮片段 */
export function parseMarkHighlight(html: string): HighlightPart[] {
  if (!html) return [];

  const parts: HighlightPart[] = [];
  const re = /<mark>(.*?)<\/mark>/gi;
  let last = 0;
  let m: RegExpExecArray | null;

  while ((m = re.exec(html)) !== null) {
    if (m.index > last) {
      parts.push({ hit: false, text: html.slice(last, m.index) });
    }
    parts.push({ hit: true, text: m[1] });
    last = m.index + m[0].length;
  }

  if (last < html.length) {
    parts.push({ hit: false, text: html.slice(last) });
  }

  return parts.length ? parts : [{ hit: false, text: html }];
}

/** 从访客搜索结果的 highlight 对象提取可展示的高亮行 */
export function extractVisitorHighlights(
  highlight: unknown,
  fieldLabels: Record<string, string> = {},
  fieldOrder: string[] = [],
): VisitorHighlightItem[] {
  const o = asRecord(highlight);
  if (!o) return [];

  const items: VisitorHighlightItem[] = [];

  for (const [field, raw] of Object.entries(o)) {
    if (!Array.isArray(raw) || raw.length === 0) continue;
    const html = toStr(raw[0]);
    if (!html) continue;

    const parts = parseMarkHighlight(html);
    if (!parts.some((p) => p.text)) continue;

    items.push({
      field,
      label: fieldLabels[field] || field,
      parts,
    });
  }

  items.sort((a, b) => {
    const ai = fieldOrder.indexOf(a.field);
    const bi = fieldOrder.indexOf(b.field);
    const aOrder = ai === -1 ? 999 : ai;
    const bOrder = bi === -1 ? 999 : bi;
    if (aOrder !== bOrder) return aOrder - bOrder;
    return a.field.localeCompare(b.field);
  });

  return items;
}

/** 按关键字拆分用于高亮（大小写不敏感）；keyword 为空则整段为 plain */
export function splitHighlight(text: string, keyword: string): HighlightPart[] {
  const q = keyword.trim();
  if (!q || !text) return [{ hit: false, text: text || "" }];

  const escaped = q.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  const re = new RegExp(escaped, "gi");
  const parts: HighlightPart[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const copy = new RegExp(re.source, re.flags);
  while ((m = copy.exec(text)) !== null) {
    if (m.index > last) {
      parts.push({ hit: false, text: text.slice(last, m.index) });
    }
    parts.push({ hit: true, text: m[0] });
    last = m.index + m[0].length;
    if (m[0].length === 0) copy.lastIndex++;
  }
  if (last < text.length) {
    parts.push({ hit: false, text: text.slice(last) });
  }
  return parts.length ? parts : [{ hit: false, text }];
}
